import React, { useMemo, useState } from 'react';
import { Box, Typography, IconButton, Paper, Stack, Button, Divider } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventRow from './EventRow';
import { groupEventsByDate, formatDateHeading, formatTime, isSameDay } from './eventUtils';

const WEEKDAYS = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];

const buildMonthMatrix = (year, month) => {
  const firstOfMonth = new Date(year, month, 1);
  const startDay = (firstOfMonth.getDay() + 6) % 7; // Monday = 0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const cells = [];
  for (let i = 0; i < startDay; i++) {
    const day = daysInPrevMonth - startDay + 1 + i;
    cells.push({ date: new Date(year, month - 1, day), inMonth: false });
  }
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push({ date: new Date(year, month, day), inMonth: true });
  }
  let nextDay = 1;
  while (cells.length % 7 !== 0) {
    cells.push({ date: new Date(year, month + 1, nextDay), inMonth: false });
    nextDay += 1;
  }
  return cells;
};

const EventCalendarView = ({ events }) => {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(today);

  const eventsByDay = useMemo(() => groupEventsByDate(events), [events]);
  const cells = useMemo(() => buildMonthMatrix(cursor.getFullYear(), cursor.getMonth()), [cursor]);
  const monthLabel = cursor.toLocaleDateString('nl-BE', { month: 'long', year: 'numeric' });

  const selectedEvents = (eventsByDay.get(selectedDate.toDateString()) || [])
    .slice()
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
        <IconButton onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="h6" sx={{ textTransform: 'capitalize', minWidth: 180, textAlign: 'center' }}>
          {monthLabel}
        </Typography>
        <IconButton onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>
          <ChevronRightIcon />
        </IconButton>
        <Button
          size="small"
          onClick={() => {
            setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
            setSelectedDate(today);
          }}
        >
          Vandaag
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
        {WEEKDAYS.map((d) => (
          <Typography key={d} variant="caption" sx={{ textAlign: 'center', fontWeight: 600, color: 'text.secondary' }}>
            {d}
          </Typography>
        ))}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
        {cells.map(({ date, inMonth }) => {
          const dayEvents = (eventsByDay.get(date.toDateString()) || [])
            .slice()
            .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
          const isToday = isSameDay(date, today);
          const isSelected = isSameDay(date, selectedDate);

          return (
            <Paper
              key={date.toISOString()}
              onClick={() => setSelectedDate(date)}
              variant="outlined"
              sx={{
                minHeight: 92,
                p: 0.75,
                cursor: 'pointer',
                opacity: inMonth ? 1 : 0.45,
                borderColor: isSelected ? 'primary.main' : 'divider',
                borderWidth: isSelected ? 2 : 1,
                '&:hover': { borderColor: 'primary.main' },
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: isToday ? 700 : 400, color: isToday ? 'primary.main' : 'text.primary' }}
              >
                {date.getDate()}
              </Typography>
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {dayEvents.slice(0, 2).map((event) => (
                  <Box
                    key={event.id}
                    sx={{
                      fontSize: '0.7rem',
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      borderRadius: 0.5,
                      px: 0.5,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {formatTime(event.start_time)} {event.name}
                  </Box>
                ))}
                {dayEvents.length > 2 && (
                  <Typography variant="caption" color="text.secondary">
                    +{dayEvents.length - 2} meer
                  </Typography>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 1.5, textTransform: 'capitalize' }}>
          {formatDateHeading(selectedDate)}
        </Typography>
        {selectedEvents.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Geen events op deze dag.
          </Typography>
        ) : (
          <Paper elevation={2}>
            {selectedEvents.map((event, idx) => (
              <React.Fragment key={event.id}>
                {idx > 0 && <Divider />}
                <EventRow event={event} />
              </React.Fragment>
            ))}
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default EventCalendarView;
